import { useRef, useState } from 'react'
import { ChevronsUpDown } from 'lucide-react'
import { ClickableRow, TD_CLASS } from '@/components/ui/ListTable'
import { useAuth } from '@/contexts/AuthContext'
import { useAssignIssue, useUpdateIssuePriority } from '../../hooks/useSystemIssues'
import { errorToast } from '../../../occurrences/utils/error-toast'
import { Avatar } from '../../../occurrences/components/common/Avatar'
import { formatDateTime as formatShort } from '../../../occurrences/utils/date-format'
import type {
  SystemIssue,
  SystemIssuePriority,
  SystemIssueStatus,
} from '../../types/system-issues'
import { AssignDeveloperPicker } from './AssignDeveloperPicker'

export const STATUS_LABELS: Record<SystemIssueStatus, string> = {
  open: 'Aberto',
  pending: 'Pendente',
  in_progress: 'Em andamento',
  improvement_suggestion: 'Sugestão de Melhoria',
  resolved: 'Resolvido',
}
export const STATUS_DOT_CLASS: Record<SystemIssueStatus, string> = {
  open: 'bg-green-500',
  pending: 'bg-orange-500',
  in_progress: 'bg-blue-600',
  improvement_suggestion: 'bg-purple-500',
  // Verde-azulado: "concluído", sem confundir com o verde de "Aberto".
  resolved: 'bg-teal-500',
}

/** Mesmos 4 níveis da fila de Solicitações/Melhorias. "Média" fica neutra
 * (cinza); "Baixa" é só o contorno. */
export const PRIORITY_LABELS: Record<SystemIssuePriority, string> = {
  urgent: 'Urgente',
  high: 'Alta',
  medium: 'Média',
  low: 'Baixa',
}
export const PRIORITY_DOT_CLASS: Record<SystemIssuePriority, string> = {
  urgent: 'bg-red-500',
  high: 'bg-orange-500',
  medium: 'bg-gray-400',
  low: 'border-[1.5px] border-gray-400',
}
export const PRIORITY_OPTIONS: SystemIssuePriority[] = ['urgent', 'high', 'medium', 'low']

/** Data e hora com ano ("22/09/2026, 14:43") — propriedades da thread. */
export function formatDateTime(value: string): string {
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

interface IssueRowProps {
  issue: SystemIssue
  onSelect: (issueId: number) => void
  /** Coluna "Reportado por" (nome + empresa) — some só em "meus problemas". */
  showReporterMeta?: boolean
  /** Dev: prioridade editável e "Atribuir a…" na linha. */
  canEditPriority?: boolean
  /** Aba HUB do processo: quem reportou vai para a linha do #id (no lugar do
   * processo, que é o próprio) e "Aberto" sai. */
  compact?: boolean
}

/**
 * Linha da fila do Atendimento HUB: ponto de não lida, chamado (#id ·
 * processo), quem reportou, atribuição, status, prioridade, abertura e
 * última mensagem.
 */
export function IssueRow({
  issue,
  onSelect,
  showReporterMeta = false,
  canEditPriority = false,
  compact = false,
}: IssueRowProps) {
  const unread = issue.unread_count ?? 0
  const lastAt = issue.last_message_at ?? issue.created_at
  const processNumbers = issue.related_processes.map((p) => p.process_number ?? `#${p.id}`)

  return (
    // `ClickableRow` (não <button>): a linha contém controles.
    <ClickableRow onClick={() => onSelect(issue.id)}>
      <td className="w-10 py-3 pl-5 pr-0 align-middle">
        {unread > 0 && (
          <span className="block h-2 w-2 rounded-full bg-blue-600" aria-label="Não lida" />
        )}
      </td>
      <td className={TD_CLASS}>
        <div className="max-w-[22rem] truncate text-sm font-semibold" title={issue.title}>
          {issue.title}
        </div>
        <div className="mt-0.5 text-xs text-muted">
          #{issue.id}
          {compact && issue.created_by && ` · ${issue.created_by.full_name}`}
          {!compact && processNumbers.length > 0 && (
            <>
              {' · '}
              {/* O primeiro e "+N"; todos na dica. */}
              <span className="font-mono font-medium text-body/80" title={processNumbers.join(', ')}>
                {processNumbers[0]}
              </span>
              {processNumbers.length > 1 && ` +${processNumbers.length - 1}`}
            </>
          )}
        </div>
      </td>
      {showReporterMeta && !compact && (
        <td className={TD_CLASS}>
          <div className="max-w-[12rem] truncate">{issue.created_by?.full_name ?? '—'}</div>
          {issue.company?.name && (
            <div className="max-w-[12rem] truncate text-xs uppercase text-muted">
              {issue.company.name}
            </div>
          )}
        </td>
      )}
      <td className={TD_CLASS}>
        <AssigneeCell issue={issue} canAssign={canEditPriority} />
      </td>
      <td className={TD_CLASS}>
        <span className="inline-flex items-center gap-1.5">
          <span className={`h-2 w-2 shrink-0 rounded-full ${STATUS_DOT_CLASS[issue.status]}`} aria-hidden />
          {STATUS_LABELS[issue.status]}
        </span>
      </td>
      <td className={TD_CLASS}>
        <PriorityCell issue={issue} editable={canEditPriority} />
      </td>
      {!compact && (
        <td className={`${TD_CLASS} tabular-nums text-muted`}>{formatShort(issue.created_at)}</td>
      )}
      <td className={TD_CLASS}>
        <span className="inline-flex items-center gap-2">
          <span className={`tabular-nums ${unread > 0 ? 'font-semibold text-body' : 'text-muted'}`}>
            {formatShort(lastAt)}
          </span>
          {unread > 0 && (
            <span
              className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-blue-600 px-1.5 text-[11px] font-semibold tabular-nums text-white"
              title={`${unread} mensage${unread === 1 ? 'm' : 'ns'} não lida${unread === 1 ? '' : 's'}`}
            >
              {unread > 99 ? '99+' : unread}
            </span>
          )}
        </span>
      </td>
    </ClickableRow>
  )
}

/** "Você", o nome do atribuído ou — para dev — "Atribuir a…". */
function AssigneeCell({ issue, canAssign }: { issue: SystemIssue; canAssign: boolean }) {
  const { user } = useAuth()
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLButtonElement>(null)
  const assign = useAssignIssue(issue.id)
  const assignee = issue.assigned_to
  const isMe = !!assignee && assignee.id === user?.id
  const label = assignee ? (
    <span className="inline-flex max-w-[12rem] items-center gap-2" title={assignee.full_name}>
      <Avatar name={assignee.full_name} picture={assignee.picture} size={28} variant="soft" />
      <span className="truncate">{isMe ? 'Você' : assignee.full_name}</span>
    </span>
  ) : null

  if (!canAssign) return label ?? <span className="text-subtle">—</span>
  return (
    <>
      <button
        ref={ref}
        type="button"
        onClick={(e) => {
          e.stopPropagation()
          setOpen((o) => !o)
        }}
        className={
          label
            ? 'rounded-md text-left transition hover:opacity-80'
            : 'rounded-md border border-dashed border-border-strong px-3 py-1.5 text-[13px] text-muted transition hover:border-gray-400 hover:text-body'
        }
      >
        {label ?? 'Atribuir a…'}
      </button>
      {open && (
        <AssignDeveloperPicker
          currentId={issue.assigned_to_id}
          anchorRef={ref}
          onPick={(id) => assign.mutate(id, { onError: errorToast('Não foi possível atribuir.') })}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  )
}

/** Prioridade: ponto + rótulo; dev troca pelo select. */
function PriorityCell({ issue, editable }: { issue: SystemIssue; editable: boolean }) {
  const updatePriority = useUpdateIssuePriority(issue.id)
  const dot = (
    <span className={`h-2 w-2 shrink-0 rounded-full ${PRIORITY_DOT_CLASS[issue.priority]}`} aria-hidden />
  )
  if (!editable) {
    return (
      <span className="inline-flex items-center gap-1.5">
        {dot}
        {PRIORITY_LABELS[issue.priority]}
      </span>
    )
  }
  return (
    <div className="relative inline-flex items-center gap-1.5 pr-4">
      {dot}
      <select
        value={issue.priority}
        disabled={updatePriority.isLoading}
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => {
          e.stopPropagation()
          updatePriority.mutate(e.target.value as SystemIssuePriority, {
            onError: errorToast('Não foi possível mudar a prioridade.'),
          })
        }}
        aria-label="Prioridade"
        // `field-sizing`: a largura acompanha a opção escolhida, não a maior.
        className="cursor-pointer appearance-none bg-transparent outline-none [field-sizing:content] disabled:cursor-wait"
      >
        {PRIORITY_OPTIONS.map((p) => (
          <option key={p} value={p}>
            {PRIORITY_LABELS[p]}
          </option>
        ))}
      </select>
      <ChevronsUpDown className="pointer-events-none absolute right-0 h-2.5 w-2.5 opacity-60" aria-hidden />
    </div>
  )
}
